// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback, useMemo } from 'react'
import { formatUsDate, formatUsDateTime } from '../api/format'
import { useSearchParams } from 'react-router-dom'
import { ClipboardPlus, FileText, FileSignature, Loader2, ArrowLeft } from 'lucide-react'
import * as api from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { friendlyError } from '../api/errors'
import PlanOfCareWizard from './carerx/PlanOfCareWizard'
import PlanOfCareSendDialog from './carerx/PlanOfCareSendDialog'
import { useCareRxSigning } from '../hooks/queries/inappEsign'
import { allowsDocuSign } from '../api/inappEsign'
import { esignErrorText } from '../api/esignErrors'
import { DocuSignOffNote } from '../components/carerx/SigningNotes'
import ReviewAssignee from './carerx/ReviewAssignee'
import { assignReviewTo } from '../api/carerxClinical'
import CareRxBilling from './carerx/CareRxBilling'
import CareRxInsights from './carerx/CareRxInsights'
import CareRxClinicalReview from './carerx/CareRxClinicalReview'
import CareRxScreeningHoldV2 from './carerx/CareRxScreeningHoldV2'
import CareRxConsentFulfillment from './carerx/CareRxConsentFulfillment'
import CareRxLabTracker from './carerx/CareRxLabTracker'
import CareRxFormulary from './carerx/CareRxFormulary'
import CareRxRenewals from './carerx/CareRxRenewals'
import CareRxPotWorklist from './carerx/CareRxPotWorklist'
import EnrollmentDoor from './carerx/EnrollmentDoor'
import { identityPreset, patientIdentityLabel, queueIdentity, type PatientIdentity } from './carerx/patientIdentity'
import SidePanel from '../components/common/SidePanel'
import PatientEnrollmentPdfButton from './people/PatientEnrollmentPdfButton'
import TherapyDocumentButtons from './carerx/TherapyDocumentButtons'
import SearchableSelect from '../components/common/SearchableSelect'
import { rowOpen, rowActions } from './carerx/rowOpen'
import { useServerPaged, Pager, DEFAULT_PAGE_SIZE } from '../components/Pager'
import { useTableFunnels, useServerFunnels, type FunnelColumn, type ServerFunnelColumn } from '../components/common/TableFunnels'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { useModules } from '../auth/ModulesContext'
// G-9: pdf-lib + the embedded form template load lazily — only on Draft PDF click.
// Tenant brand rides along so the draft restamps the pharmacy's own identity (owner ruling 2026-08-14).
import CareRxUat from './carerx/CareRxUat'
import CareRxAdminProtocols from './carerx/CareRxAdminProtocols'
import CareRxAllNurses from './carerx/CareRxAllNurses'
import CareRxStaffing from './carerx/CareRxStaffing'
import CareRxCoverageAreas from './carerx/CareRxCoverageAreas'
import CareRxSupplyCatalog from './carerx/CareRxSupplyCatalog'
import CareRxAiConsentLog from './carerx/CareRxAiConsentLog'
import { CHIP_HINT, ENROLL_CHIPS, chipCount, inChip, landingChip, stageText, type EnrollChip } from './carerx/enrollmentStage'
import { EnrollmentStageAction, EnrollmentStageStatus } from './carerx/EnrollmentStageCells'
import { useEnrollmentQueue } from '../hooks/queries/carerxEnrollmentQueue'
import Notice from '../components/ui/Notice'
import { useIsNarrow } from '../hooks/useIsNarrow'
import NurseAssign from './carerx/NurseAssign'
import LanguageBadge from '../components/ui/LanguageBadge'
import TherapyRefTag from '../components/ui/TherapyRefTag'
import EnrollmentReviewNotes from './carerx/EnrollmentReviewNotes'
import TruncatedText from '../components/ui/TruncatedText'
import TruncationNotice from '../components/ui/TruncationNotice'
import { useClinicalCounts } from '../hooks/queries/carerxClinical'
import { DismissCandidateButton, DismissedCandidatesPanel } from './carerx/FeedDismissals'
import { beforeStartLine, type RxStartInfo } from '../lib/rxStartDate'
import AttachRxButton from './carerx/AttachRxButton'
import TherapyRxList from './carerx/TherapyRxList'
import EnrollmentValiditySetting from './carerx/EnrollmentValiditySetting'

interface Props { onScreenEnter?: () => void }

const STATUS_COLORS: Record<string, string> = {
  draft: '#6b7280', in_progress: '#2563eb', pending_signature: '#d97706',
  signed: '#059669', completed: '#059669', cancelled: '#dc2626',
}

export type TabKey = 'dashboard' | 'my-visits' | 'assessments' | 'enrollment' | 'clinical' | 'screening' | 'fulfillment' | 'labs' | 'formulary' | 'renewals' | 'pcw' | 'pot' | 'staffing' | 'coverage' | 'billing' | 'admin-protocols' | 'supply' | 'nurses' | 'ai-consent' | 'uat'
// D-CRX-33 R1 (Andrew FINAL): nine tabs in workday order; engine names nowhere; Formulary + UAT
// demoted to an Admin group at the rail's end ("staff never trip over machinery"). Merged surfaces
// render by COMPOSITION: Clinical review hosts Screening & hold; POT & Plans of Care hosts the PoC
// shelf. Legacy keys (screening, assessments, pcw + old bookmarks) MAP, never 404.
const TABS: { key: TabKey; label: string; admin?: boolean }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'my-visits', label: 'My visits' },
  { key: 'enrollment', label: 'Enrollment' },
  { key: 'labs', label: 'Lab tracker' },
  { key: 'clinical', label: 'Review' },
  { key: 'screening', label: 'Hold' },
  // D-CRX-56 R-1 (owner renames) + R-2 (Staffing off the POT tab into its own).
  { key: 'fulfillment', label: 'Consent & Drug Order Form' },
  { key: 'pot', label: 'Plan of Treatment' },
  { key: 'staffing', label: 'Staffing' },
  { key: 'coverage', label: 'Coverage areas' },
  { key: 'billing', label: 'Billing & agencies' },
  { key: 'renewals', label: 'Renewals' },
  // D-CRX-43 WS17 (Patrick 2026-07-28): Formulary tab hidden — Drug Reference in Pharmacy Settings
  // is the single drug-management surface. The table and endpoints stay; deep links fall through below.
  { key: 'nurses', label: 'All nurses', admin: true },
  { key: 'admin-protocols', label: 'Admin protocols', admin: true },
  { key: 'supply', label: 'Supply catalog', admin: true },
  { key: 'ai-consent', label: 'AI consent', admin: true },
  { key: 'uat', label: 'UAT', admin: true },
]
// Surfaces that belong to the DEV TEAM, not to a pharmacy. UAT drives the internal
// acceptance harness and means nothing to a customer looking at their own CareRx —
// it is hidden unless the viewer is a platform operator. Hidden, not deleted: the
// tab, its route and its ?tab=uat deep link all still work for whoever holds
// platform.access.
const DEV_ONLY_TABS = new Set<TabKey>(['uat'])
// P0 B11 (owner walkthrough 2026-10-04): a nurse opening CareRx got every staff tab, and the server
// refused what was behind them (8 x 403: dashboard, insights, Plans of Care…). A nurse works her visits:
// the POT queue and My visits. Everything else stays server-gated as before; this only stops offering it.
export const NURSE_TABS = new Set<TabKey>(['pot', 'my-visits'])
// Owner 2026-10-05: Billing & Agency, Admin and AI consent moved to the sidebar's "CareRx admin" group,
// pharmacy admins only. They keep their ?tab= deep links; the rail no longer draws them (a nurse keeps
// her My visits entry there — NURSE_TABS decides for her).
export const ADMIN_TABS = new Set<TabKey>(['billing', 'my-visits', 'staffing', 'coverage', 'nurses', 'admin-protocols', 'supply', 'ai-consent'])
const RAIL_KEYS = new Set(TABS.map(t => t.key))
// ASP #23: the Clinical stages whose tab label carries a count (GET carerx/clinical/counts).
const CLINICAL_COUNT_KEY = { clinical: 'review', renewals: 'renewals', screening: 'hold' } as const
export const TAB_LABEL = new Map(TABS.map(t => [t.key, t.label] as const))

// D-CRX-65 (owner 2026-08-25): the rail grew to 19 flat tabs and stopped being scannable. Related
// surfaces now sit under a PARENT tab that opens a second row of its children. This is deliberately
// a PRESENTATION change only: every tab keeps its own key, its own ?tab=<key> deep link and its own
// component, so bookmarks and mapLegacyTab keep working and no surface had to be re-wired.
// A parent is never a destination — clicking it lands on its first visible child.
// rail: false — reached from the sidebar, not drawn on the rail (its child row still shows when open).
type NavEntry =
  | { kind: 'tab'; key: TabKey; rail?: false }
  | { kind: 'group'; key: string; label: string; children: TabKey[]; rail?: false }
export const NAV: NavEntry[] = [
  { kind: 'tab', key: 'dashboard' },
  { kind: 'tab', key: 'enrollment' },
  { kind: 'tab', key: 'labs' },
  // D-CRX-68: Clinical is one lane with three STAGES, in the order work moves through them —
  // Review, then Renewals, then Hold. The lists are mutually exclusive server-side (a therapy on
  // hold is not also in Review), so a record sits in exactly one child tab at a time and moving
  // it forward is what makes it leave the previous one.
  { kind: 'group', key: 'g-clinical', label: 'Clinical', children: ['clinical', 'renewals', 'screening'] },
  { kind: 'tab', key: 'fulfillment' },
  { kind: 'tab', key: 'pot' },
  { kind: 'group', key: 'g-billing', label: 'Billing & Agency', children: ['billing', 'my-visits', 'staffing', 'coverage', 'nurses'], rail: false },
  // P0 B7 (UX audit 2026-10-04): 'quality' and 'accredit' left TABS in #2604 but stayed here, so the rail
  // drew two blank tabs that bounced to the Dashboard. Both screens are seeded demos with no backend
  // (P4 WP 4.5) — they stay off the rail until they are real.
  // The admin tail stays at the rail's end (D-CRX-33 R1): the Admin parent now marks that region,
  // so the old "Admin" text divider is gone rather than sitting beside a tab of the same name.
  { kind: 'group', key: 'g-admin', label: 'Admin', children: ['admin-protocols', 'supply'], rail: false },
  { kind: 'tab', key: 'ai-consent', rail: false },
  { kind: 'tab', key: 'uat' },
]
// Tabs a pharmacy can be opted out of, per-pharmacy, from the CareRx module-enable section. The
// KEYS are pharmacy_modules rows (default-ON, so nobody loses a tab they have today) — this reuses
// the licensing engine rather than inventing a second switch, and the backend keeps enforcing its
// own gates regardless of what the rail shows.
const TAB_MODULE: Partial<Record<TabKey, string>> = {
  // D-POT-2 (owner 2026-10-20): the native Plan of Treatment tab and the nurse's My visits are the
  // OwlRx POT experience — off whenever the pharmacy chose POT classic (the two are exclusive;
  // /me/modules reports carerx_pot_native as NOT carerx_pot_classic).
  pot: 'carerx_pot_native', 'my-visits': 'carerx_pot_native',
}
export function mapLegacyTab(k: string | null | undefined): TabKey {
  if (!k) return 'dashboard'
  if (k === 'pcw') return 'clinical'
  if (k === 'assessments') return 'pot'
  return (RAIL_KEYS.has(k as TabKey) ? k : 'dashboard') as TabKey
}

/** What an enrollment delete did, in one line. An envelope it could not void is still live at
 *  DocuSign, so that part is a warning the operator has to act on, not a footnote. */
function deleteOutcome(r: Awaited<ReturnType<typeof api.deleteCareRxEnrollment>>): string {
  const parts = [r.had_signed_envelope
    ? 'Enrollment deleted. It had a SIGNED envelope — the executed document is retained on the chart.'
    : 'Enrollment deleted. Labs, holds and documents are retained.']
  if (r.visits_cancelled > 0) parts.push(`${r.visits_cancelled} open POT visit${r.visits_cancelled === 1 ? '' : 's'} cancelled.`)
  if (r.envelopes_voided > 0) parts.push(`${r.envelopes_voided} unsigned envelope${r.envelopes_voided === 1 ? '' : 's'} voided.`)
  const stuck = r.envelopes_not_voided ?? []
  if (stuck.length > 0)
    parts.push(`WARNING: ${stuck.length} envelope${stuck.length === 1 ? ' is' : 's are'} still out for signature at DocuSign `
      + `(${stuck.map(s => s.subject_type).join(', ')}) — DocuSign would not void ${stuck.length === 1 ? 'it' : 'them'}; void by hand.`)
  return parts.join(' ')
}

export default function CareRxPage({ onScreenEnter }: Props) {
  useEffect(() => { onScreenEnter?.() }, [onScreenEnter])
  const narrow = useIsNarrow()
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<TabKey>('dashboard')
  // Hide only what we KNOW is switched off (ModulesContext fails open), so a slow or unreachable
  // /me/modules never blanks a tab the pharmacy actually has.
  const { isDisabled } = useModules()
  const [openId, setOpenId] = useState<string | null>(null)
  const [creatingFor, setCreatingFor] = useState<{ id: string; name?: string; mrn?: string } | null>(null)

  // Deep links from the patient profile: ?assessment=<id> opens it; ?newForPatient=<id> (with
  // optional &patientName / &mrn) creates a Plan of Care for that patient and opens it.
  useEffect(() => {
    const a = params.get('assessment')
    const np = params.get('newForPatient')
    const t = params.get('tab')
    if (a) { setOpenId(a); setParams({}, { replace: true }); return }
    if (np) {
      setCreatingFor({ id: np, name: params.get('patientName') || undefined, mrn: params.get('mrn') || undefined })
      setParams({}, { replace: true })
      return
    }
    // D-CRX-33 R1: bookmarkable tabs — every legacy key maps (never a dead link)
    if (t) setTab(prev => { const m = mapLegacyTab(t); return m === prev ? prev : m })
    // The sidebar's plain "CareRx" link (no ?tab=) leaves a CareRx admin screen for the working rail.
    else setTab(prev => (ADMIN_TABS.has(prev) ? 'dashboard' : prev))
  }, [params, setParams])

  const selectTab = (k: string) => {
    const m = mapLegacyTab(k)
    setTab(m)
    setParams({ tab: m }, { replace: true })
  }

  useEffect(() => {
    if (!creatingFor) return
    let cancelled = false
    ;(async () => {
      try {
        const seed: Record<string, string> = {}
        if (creatingFor.name) {
          const parts = creatingFor.name.trim().split(/\s+/)
          seed['patient_certification.ptFirst'] = parts[0] || ''
          seed['patient_certification.ptLast'] = parts.slice(1).join(' ') || ''
        }
        const r = await api.createCareRxAssessment({ patient_id: creatingFor.id, payload: Object.keys(seed).length ? JSON.stringify(seed) : undefined })
        if (!cancelled) { setOpenId(r.id); setCreatingFor(null) }
      } catch { if (!cancelled) setCreatingFor(null) }
    })()
    return () => { cancelled = true }
  }, [creatingFor])

  const { hasPermission, role } = useAuth()
  const isPlatformOperator = hasPermission('platform.access')
  const isNurse = role === 'provider_nurse'
  const isAdmin = role === 'admin'
  const tabHidden = (k: TabKey) => {
    if (DEV_ONLY_TABS.has(k) && !isPlatformOperator) return true
    // A switched-off surface is hidden for EVERY role (a nurse's rail is pot + my-visits, both of
    // which belong to the native POT experience) — so the module check runs before the role ones.
    const m = TAB_MODULE[k]
    if (m && isDisabled(m)) return true
    if (isNurse) return !NURSE_TABS.has(k)
    return ADMIN_TABS.has(k) && !isAdmin
  }
  const kidsOf = (g: Extract<NavEntry, { kind: 'group' }>) => g.children.filter(k => !tabHidden(k))
  const onRail = (n: NavEntry) => n.rail !== false || isNurse
  const groups = NAV.filter((n): n is Extract<NavEntry, { kind: 'group' }> => n.kind === 'group')
  const activeGroup = groups.find(g => g.children.includes(tab))
  // A deep link (or a lingering tab) pointing at a switched-off surface falls back to the dashboard
  // rather than rendering a tab the pharmacy no longer has. A nurse falls back to her POT tab — unless
  // that is the switched-off surface itself (pharmacy on POT classic), then to the dashboard too.
  const shownTab: TabKey = tabHidden(tab) ? (isNurse && !tabHidden('pot') ? 'pot' : 'dashboard') : tab
  // ASP #23: how many items wait in each Clinical stage, on its tab label. Fetched only while the
  // Clinical row (or the narrow select that lists it) is on screen.
  const clinicalCounts = useClinicalCounts(narrow || activeGroup?.key === 'g-clinical')
  // Owner 2026-10-05: a sidebar admin screen is its own page — its heading and its own sub-tabs, not the
  // CareRx working rail (Dashboard, Enrollment, …). A nurse's My visits stays on her rail.
  const adminScreen = !isNurse && ADMIN_TABS.has(shownTab)
  const adminGroup = adminScreen ? groups.find(g => g.children.includes(shownTab)) : undefined
  const adminTitle = adminGroup?.label ?? TAB_LABEL.get(shownTab)
  // A group's child row. Pills rather than a second underline rail, so at a glance it is obvious which
  // level of the nav you are looking at.
  const subTabs = (g: Extract<NavEntry, { kind: 'group' }>, spacing: React.CSSProperties) => (
    <div role="tablist" aria-label={`${g.label} sections`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, ...spacing }}>
      {kidsOf(g).map(k => (
        <button key={k} role="tab" aria-selected={shownTab === k} onClick={() => selectTab(k)}
          style={{
            fontSize: 12.5, fontWeight: 600, padding: '5px 13px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${shownTab === k ? 'var(--color-primary)' : 'var(--color-border)'}`,
            background: shownTab === k ? 'var(--color-primary)' : 'var(--color-surface)',
            color: shownTab === k ? '#fff' : 'var(--color-text-soft)',
          }}>{tabLabel(k)}</button>
      ))}
    </div>
  )
  const tabLabel = (k: TabKey) => {
    const n = clinicalCounts.data?.[CLINICAL_COUNT_KEY[k as keyof typeof CLINICAL_COUNT_KEY]]
    return typeof n === 'number' ? `${TAB_LABEL.get(k)} (${n})` : TAB_LABEL.get(k)
  }

  return (
    // D-CRX-70: CareRx was capped at 1100px while Patients and Admin run to 1280 and the
    // worklists here are the widest tables in the product — Clinical Review alone is 14
    // columns, so the cap forced a horizontal scrollbar on screens with room to spare.
    // The token seam stays (define --page-max to cap every page at once); its fallback is
    // now the available width.
    <div style={{ maxWidth: 'var(--page-max, 100%)', width: '100%', margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <ClipboardPlus size={24} style={{ color: 'var(--color-primary)' }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
          {adminScreen ? <>CareRx admin <span style={{ color: 'var(--color-text-soft)', fontWeight: 500 }}>· {adminTitle}</span></> : 'CareRx'}
        </h1>
      </div>
      <p style={{ color: 'var(--color-text-soft)', margin: '0 0 18px', fontSize: 14 }}>
        {adminScreen ? 'Pharmacy admin settings for CareRx.' : 'Clinical documentation — Plan of Care, enrollment, signatures.'}
      </p>

      {openId ? (
        <AssessmentDetail id={openId} onBack={() => setOpenId(null)} />
      ) : (
        <>
          {adminScreen ? (
            /* Only this screen's own sub-tabs (none for AI consent). */
            adminGroup ? subTabs(adminGroup, { marginBottom: 18 }) : null
          ) : narrow ? (
            <select value={tab} onChange={e => selectTab(e.target.value)} aria-label="CareRx section"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 15, fontWeight: 600, marginBottom: 18 }}>
              {NAV.filter(n => onRail(n) || (n.kind === 'tab' ? n.key === shownTab : n.children.includes(shownTab))).map(n => n.kind === 'tab'
                ? (tabHidden(n.key) ? null : <option key={n.key} value={n.key}>{TAB_LABEL.get(n.key)}</option>)
                : (
                  <optgroup key={n.key} label={n.label}>
                    {kidsOf(n).map(k => <option key={k} value={k}>{tabLabel(k)}</option>)}
                  </optgroup>
                ))}
            </select>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: activeGroup ? 0 : 18 }}>
                {NAV.filter(onRail).map(n => {
                  if (n.kind === 'tab') {
                    return tabHidden(n.key) ? null : (
                      <TabBtn key={n.key} active={shownTab === n.key} onClick={() => selectTab(n.key)}>{TAB_LABEL.get(n.key)}</TabBtn>
                    )
                  }
                  const kids = kidsOf(n)
                  if (kids.length === 0) return null
                  return (
                    <TabBtn key={n.key} active={activeGroup?.key === n.key}
                      onClick={() => selectTab(kids.includes(shownTab) ? shownTab : kids[0])}>
                      {n.label} <span aria-hidden="true" style={{ fontSize: 10, opacity: .7 }}>▾</span>
                    </TabBtn>
                  )
                })}
              </div>
              {/* Child row — only for the open parent. Pills rather than a second underline rail, so
                  at a glance it is obvious which level of the nav you are looking at. */}
              {activeGroup && subTabs(activeGroup, { padding: '10px 2px 0', marginBottom: 18 })}
            </>
          )}
          {shownTab === 'dashboard' ? <CareRxInsights onNavigate={selectTab} />
            : shownTab === 'enrollment' ? <EnrollmentTab />
            : shownTab === 'clinical' ? (
              /* D-CRX-45 CR-E8/F1: Screening & hold is its own tab. Owner ruling 2026-08-08: the PCW
                 worksheet is REMOVED from Clinical review (engine + endpoints stay, on hold per TBD #5). */
              <CareRxClinicalReview />
            )
            : shownTab === 'screening' ? <CareRxScreeningHoldV2 />
            : shownTab === 'fulfillment' ? <CareRxConsentFulfillment />
            : shownTab === 'labs' ? <CareRxLabTracker />
            : shownTab === 'formulary' ? <CareRxFormulary /> /* deep-link fallback only (WS17) — not in the tab strip */
            : shownTab === 'renewals' ? <CareRxRenewals />
            // D-POT-7: one worklist (Queue → In progress → Completed → Voided). Plans of Care left this tab:
            // the PCW library is not part of the POT flow (owner 2026-10-27); AssessmentDetail and its
            // ?assessment= deep link stay for the links elsewhere.
            : shownTab === 'pot' ? <CareRxPotWorklist />
            : shownTab === 'staffing' ? <CareRxStaffing />
            : shownTab === 'coverage' ? <CareRxCoverageAreas />
            : shownTab === 'nurses' ? <CareRxAllNurses />
            // D-CRX-87b A6: the pharmacy's own enrollment validity (days) sits with the CareRx admin protocols.
            : shownTab === 'admin-protocols' ? <><EnrollmentValiditySetting /><CareRxAdminProtocols /></>
            : shownTab === 'supply' ? <CareRxSupplyCatalog />
            : shownTab === 'my-visits' ? <CareRxPotWorklist />
            : shownTab === 'ai-consent' ? <CareRxAiConsentLog />
            : shownTab === 'uat' ? <CareRxUat />
            : shownTab === 'billing' ? <CareRxBilling />
            : <CareRxInsights onNavigate={selectTab} />}
        </>
      )}
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, flexShrink: 0,
      fontWeight: active ? 700 : 500, color: active ? 'var(--color-primary)' : 'var(--color-text-soft)',
      borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: -1,
    }}>{children}</button>
  )
}

function StatusPill({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || '#6b7280'
  return <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
    color: c, border: `1px solid ${c}`, whiteSpace: 'nowrap',
  }}>{status.replace(/_/g, ' ')}</span>
}

// ASP #6: the CareRx warning palette (CareRxScreeningPanel, LabPanel) — no --color-warning-bg token yet.
const PREGNANT_ROW_BG = '#fffbeb'
const pregnantBadge: React.CSSProperties = {
  display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 10, fontSize: 10.5, fontWeight: 600,
  whiteSpace: 'nowrap', verticalAlign: 'middle', background: '#fde68a', color: '#92400e',
}
const ENROLL_QUEUE_FUNNELS: FunnelColumn<api.CareRxEnrollmentQueueRow>[] = [
  { key: 'Patient', label: 'Patient', value: e => e.patient_name || '—' },
  // ASP #25: every open therapy's display-only ref; the contains box finds it with or without ENR-.
  { key: 'Enrollment ref', label: 'Enrollment ref', value: e => (e.therapy_refs ?? []).join(', ') || '—', kind: 'text' },
  { key: 'Drug / archetype', label: 'Drug / archetype', value: e => e.drug_name || '—' },
  // D-CRX-84 R2: the funnel filters on the words the cell shows — the prescription's stage.
  { key: 'Status', label: 'Status', value: e => stageText(e) },
  // Owner 2026-10-04: every enrollment has a pharmacist; a row without one says so (and filters as such).
  { key: 'Pharmacist', label: 'Pharmacist', value: e => e.pharmacist_name || (e.assigned_rph_id ? 'assigned' : 'Unassigned') },
  { key: 'Assigned nurse', label: 'Assigned nurse', value: e => e.nurse_name || (e.assigned_nurse_id ? 'assigned' : '—') },
  // Owner 2026-10-01: the clinical (intake) notes and the latest review note, in full for the filter.
  { key: 'Clinical notes', label: 'Clinical notes', value: e => e.intake_notes || '—', kind: 'text' },
  { key: 'Last review', label: 'Last review', value: e => e.last_review_note || '—', kind: 'text' },
]
// D-UI-2: the PA feed is SERVER-paged, so its funnels only collect what was asked for; the
// endpoint applies them at PATIENT grain (the feed's visible row), the way its search does.
// Priority has no funnel: it is a per-row input held in this tab, not a value the server has.
const PA_FEED_FUNNELS: ServerFunnelColumn[] = [
  { key: 'patient_drug', label: 'Patient / Drug', kind: 'text' },
  { key: 'date_of_birth', label: 'DOB', kind: 'date' },
  { key: 'prescriber', label: 'Prescriber', kind: 'text' },
  { key: 'date_written', label: 'Written', kind: 'date' },
  { key: 'pa_approved', label: 'PA approved', kind: 'date' },
]

function AssessmentDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [a, setA] = useState<Record<string, any> | null>(null)
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(true) // open the wizard by default — no extra click

  const load = useCallback(async () => {
    setLoading(true)
    try { const d = await api.getCareRxAssessment(id); setA(d.assessment) }
    catch (e: any) { setMsg(friendlyError(e)) }
    finally { setLoading(false) }
  }, [id])
  useEffect(() => { load() }, [load])

  const parsedPayload = useMemo(() => {
    if (!a?.payload) return {}
    try { return typeof a.payload === 'string' ? JSON.parse(a.payload) : a.payload } catch { return {} }
  }, [a?.payload])

  const renderDoc = async () => {
    setBusy(true); setMsg(null)
    try { const r = await api.renderCareRxDocument(id); setMsg(`Plan of Care generated (hash ${r.content_hash.slice(0, 12)}…, ${r.byte_size} bytes${r.stored ? ', stored' : ''}).`) }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  // P0 B4: the recipient is chosen in PlanOfCareSendDialog (it was hard-wired to physician@example.com).
  const [sendOpen, setSendOpen] = useState(false)
  const [sendErr, setSendErr] = useState<string | null>(null)
  const signing = useCareRxSigning()
  const sendSig = async (recipient: { recipient_name: string; recipient_email: string }, saveToProfile: boolean) => {
    setBusy(true); setMsg(null); setSendErr(null)
    try {
      await api.sendCareRxForSignature(id, recipient)
      // Same as the other send dialogs: a typed address is kept on the prescriber when asked.
      if (saveToProfile && a?.prescriber_id) await api.addContactEmail('prescriber', String(a.prescriber_id), recipient.recipient_email).catch(() => {})
      setSendOpen(false)
      setMsg(`Sent to ${recipient.recipient_name} (${recipient.recipient_email}) for signature.`); await load()
    } catch (e: any) {
      setSendErr(esignErrorText(e))
    } finally { setBusy(false) }
  }

  if (loading) return <Centered><Loader2 className="spin" size={20} /> Loading…</Centered>
  if (!a) return <Centered>{msg || 'Not found'}</Centered>

  return (
    <div>
      <button style={ghostBtn} onClick={onBack}><ArrowLeft size={14} /> Back to list</button>
      <div style={{ ...panel, marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 16 }}>Plan of Care</strong>
          <StatusPill status={a.status} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 20px', margin: '14px 0', fontSize: 13 }}>
          <KV k="Drug / therapy" v={a.drug_name} />
          <KV k="Diagnosis" v={a.diagnosis} />
          <KV k="Step" v={`${a.current_step} / ${a.total_steps}`} />
          <KV k="Signature" v={a.signature_status} />
          <KV k="Created" v={formatUsDate(a.created_at as string)} />
          <KV k="Updated" v={formatUsDate(a.updated_at as string)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={primaryBtn} disabled={busy} onClick={() => setEditing(e => !e)}>
            {editing ? 'Close editor' : 'Edit Plan of Care'}
          </button>
          <button style={ghostBtn} disabled={busy} onClick={renderDoc}><FileText size={14} /> Generate Plan of Care</button>
          {/* D-CRX-87 §4d: under in_app a Plan of Care cannot be sent (the provider cannot sign in OwlRx yet). */}
          {allowsDocuSign(signing.mode)
            ? <button style={ghostBtn} disabled={busy} onClick={() => { setMsg(null); setSendErr(null); setSendOpen(true) }}><FileSignature size={14} /> Send for signature</button>
            : <DocuSignOffNote />}
          <NurseAssign currentNurseId={a.assigned_nurse_id} onAssign={async (nurseId) => {
            await api.updateCareRxAssessment(id, { assigned_nurse_id: nurseId }); await load()
          }} />
        </div>
        {editing && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--color-border)', paddingTop: 16 }}>
            <PlanOfCareWizard
              assessmentId={id}
              initialStep={a.current_step || 1}
              initialPayload={parsedPayload}
              onSaved={load}
            />
          </div>
        )}
        {msg && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--color-text-soft)', background: 'var(--color-surface-alt)', padding: '8px 12px', borderRadius: 8 }}>{msg}</div>}
      </div>
      {sendOpen && <PlanOfCareSendDialog open prescriberId={a.prescriber_id as string | null}
        prescriberName={a.prescriber_name as string | null} busy={busy} error={sendErr}
        mode={signing.mode} docusignReady={signing.docusignReady}
        onCancel={() => setSendOpen(false)} onSend={sendSig} />}
    </div>
  )
}

export function EnrollmentTab() {
  // D-CRX-80: the delete control is gated on the PERMISSION (carerx.enrollment.delete, mig 386),
  // never on a role name — the grant is the single source of truth and roles change.
  const { hasPermission, me } = useAuth()
  const [doorOpen, setDoorOpen] = useState(false)
  const [doorPrefill, setDoorPrefill] = useState<{ patient: any; language?: string | null; formularyId?: string; rxNumber?: string | null; priority?: string } | null>(null)
  // D-CRX-83 (Mario UAT 2026-10-04, supersedes ASP #10): the PA feed no longer sits above the queue.
  // "+ New enrollment" opens a chooser: From PA approved (the same server-paged feed) or Manual.
  // Picking either opens the enrollment door; Cancel on the door brings the chooser back, a created
  // enrollment does not (the door stays open on its success panel, then closes to the queue).
  const [chooserOpen, setChooserOpen] = useState(false)
  const [chooser, setChooser] = useState<'pa' | 'manual'>('pa')
  const [doorFromChooser, setDoorFromChooser] = useState(false)
  const [doorCreated, setDoorCreated] = useState(false)
  // E-02: the queue is a TanStack query. D-CRX-84 R2: each row carries its server-computed stage and bucket.
  const queueQ = useEnrollmentQueue()
  const items = useMemo(() => queueQ.data?.items ?? [], [queueQ.data])
  const queueCap = queueQ.data?.truncated ? (queueQ.data.cap ?? items.length) : null  // only when the server truncated
  // W2-C: the server's whole-queue counts per bucket (the chips must not count only 500 rows)
  const bucketCounts = queueQ.data?.bucket_counts
  const loading = queueQ.isPending
  const error = queueQ.isError ? friendlyError(queueQ.error) : null
  // A row action's outcome, shown above the chips (the review panel's message sits inside the dialog).
  const [queueNote, setQueueNote] = useState<{ text: string; ok: boolean } | null>(null)
  // Owner review 2026-08-13: the panel headers name the patient. Wave-1 #1: the WHOLE identity
  // (id, name, DOB) is the selection — a loose name string was dropped on the post-edit reopen.
  const [selPatient, setSelPatient] = useState<PatientIdentity | null>(null)
  const sel = selPatient?.id ?? null
  const selLabel = patientIdentityLabel(selPatient)
  const [detail, setDetail] = useState<Record<string, any> | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [bucket, setBucket] = useState<EnrollChip>('review')
  // UAT 2026-07-29 #5: edit + draft-PDF per active therapy, right from the queue's review panel.
  const [selTherapies, setSelTherapies] = useState<api.CareRxTherapyLane[]>([])
  const [editTarget, setEditTarget] = useState<api.CareRxTherapyLane | null>(null)
  // D-CRX-80: the therapy queued for a SOFT delete, and the reason the actor must give. A
  // deletion with no reason tells the next person nothing, and this is the record an audit reads.
  const [deleteTarget, setDeleteTarget] = useState<api.CareRxTherapyLane | null>(null)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [unclassified, setUnclassified] = useState(0)
  const [feedQ, setFeedQ] = useState('')
  // Server-side search: the page you are looking at is a WINDOW now, so a client-side filter
  // would only ever match the ~25 rows already fetched and quietly hide the rest.
  // Ticket 123qmzr1yyb: a one-letter search matches most of the feed and is the slowest query it
  // runs, so it waits for a second character; clearing the box still searches (shows everything).
  const feedQTyped = useDebouncedValue(feedQ, 400)
  const feedQDebounced = feedQTyped.trim().length === 1 ? '' : feedQTyped
  const [feedPriority, setFeedPriority] = useState<Record<string, string>>({})  // CR-G2 per-patient priority in the PA queue
  const [starting] = useState<string | null>(null)  // retained: disables Start during in-flight legacy paths

  const load = () => { void queueQ.refetch() }
  // Owner UI surf 2026-08-13: landing on "Needs review (0)" made a populated queue look empty.
  // On first load only, fall through to the first chip that actually has rows.
  const [landed, setLanded] = useState(false)
  useEffect(() => {
    if (landed || !queueQ.data) return
    setLanded(true)
    setBucket(prev => prev === 'review' ? landingChip(queueQ.data.bucket_counts, queueQ.data.items) : prev)
  }, [landed, queueQ.data])
  const [unqualified, setUnqualified] = useState(0)
  // D-CRX-87 §2: the pharmacy's Rx start date and how many qualified Rx it keeps off the feed.
  const [rxStart, setRxStart] = useState<RxStartInfo | null>(null)
  // ASP #14: prescriptions removed from this list (restorable from the "Removed from list" panel).
  const [dismissedCount, setDismissedCount] = useState(0)
  const [dismissedOpen, setDismissedOpen] = useState(false)
  // D-PERF-1 P2: the PA feed is server-paged (patient grain — one visible row = one patient, all
  // their qualified drugs together). It used to render every candidate the server's TOP 200 cap
  // let through, which both hid the overflow and put hundreds of rows in the DOM at once.
  // Rows-per-page (10/25/50/100) is the shared sticky preference every other table uses.
  const feedFx = useServerFunnels(PA_FEED_FUNNELS)
  const candidatesPaged = useServerPaged<api.CareRxEnrollmentCandidate>(
    ({ page, pageSize, signal }) => api.listCareRxEnrollmentCandidates({ page, pageSize, q: feedQDebounced, filter: feedFx.filter, signal })
      .then(d => {
        setUnclassified(d.unclassified_drugs ?? 0); setUnqualified(d.unqualified_count ?? 0)
        setDismissedCount(d.dismissed_count ?? 0)
        setRxStart(d)
        return d
      })
      // Ticket 123qmzr1yyb: a failed fetch keeps the rows already on screen (the hook leaves them and
      // sets .error) instead of replacing them with "No PA-approved candidates." — a busy database
      // read as an empty feed. A DB shape without rx_items is answered by the SERVER with an empty
      // page, so that case still renders empty.
      ,
    DEFAULT_PAGE_SIZE,
    [feedQDebounced, feedFx.sig],
  )
  const candidates = candidatesPaged.rows
  const candidateTotal = candidatesPaged.total ?? candidates.length   // prescriptions — one row each
  // Refetch the page you are on (a removal or an enrollment must not send you back to page 1).
  const loadCandidates = candidatesPaged.reload
  // Start OPENS the door prefilled with THIS prescription (owner ruling 2026-10-01: one enrollment
  // per patient per Rx), carrying the queue-chosen priority (CR-G2). Shared by the Start button and
  // the row click so both do exactly the same thing.
  const startFromCandidate = (c: api.CareRxEnrollmentCandidate) => {
    setDoorPrefill({
      // EnrollmentDoor renders presetPatient.label — display_name alone left the "Patient:"
      // header blank (walkthrough find, 2026-08-11).
      patient: { ...identityPreset({ id: c.patient_id, name: c.patient_name || 'Patient', dob: c.date_of_birth }), display_name: c.patient_name || 'Patient' },
      language: c.preferred_language,
      formularyId: c.formulary_id || undefined,
      rxNumber: c.rx_number,
      priority: feedPriority[c.rx_id] ?? 'normal',
    })
    setChooserOpen(false); setEditTarget(null); setMsg(null)
    setDoorFromChooser(true); setDoorCreated(false); setDoorOpen(true)
  }
  const startManual = () => {
    setDoorPrefill(null); setChooserOpen(false); setEditTarget(null); setMsg(null)
    setDoorFromChooser(true); setDoorCreated(false); setDoorOpen(true)
  }
  // PA approved is the default whenever there is anything in it.
  const openChooser = () => { setChooser(candidateTotal > 0 ? 'pa' : 'manual'); setChooserOpen(true); loadCandidates() }
  const closeDoor = () => {
    setDoorOpen(false); setDoorPrefill(null); loadCandidates()
    if (doorFromChooser && !doorCreated) setChooserOpen(true)
    setDoorFromChooser(false)
  }

  // ZZTEST fence. The SERVER gate is the CARERX_ENROLLMENT_FENCE app setting, and
  // POST /carerx/enrollments already honours it. This feed used to hide Start on
  // `is_synthetic` alone and never asked for the fence state, so lifting the setting
  // changed nothing a user could see — real patients stayed view-only forever.
  // Fail CLOSED: any error, or a state we cannot read, leaves the fence active.
  const [fenceLifted, setFenceLifted] = useState(false)
  useEffect(() => {
    api.getCareRxGovernance()
      .then(g => setFenceLifted(g?.fence?.state === 'lifted'))
      .catch(() => setFenceLifted(false))
  }, [])

  const openReview = async (p: PatientIdentity) => {
    const pid = p.id
    setSelPatient(p); setMsg(null); setDetail(null)
    setSelTherapies([]); setEditTarget(null)
    api.listCareRxTherapies(pid).then(r => setSelTherapies((r.therapies ?? []).filter(t => !t.is_terminal))).catch(() => setSelTherapies([]))
    try {
      const d = await api.getCareRxEnrollment(pid); setDetail(d.enrollment)
    } catch { /* ignore */ }
  }
  // D-UI-2: the bucket chip picks the set; the funnels then filter that WHOLE set.
  const shown = useMemo(() => items.filter(e => inChip(bucket, e)), [items, bucket])
  const queueFx = useTableFunnels(shown, ENROLL_QUEUE_FUNNELS)
  if (loading) return <Centered><Loader2 className="spin" size={20} /> Loading…</Centered>
  if (error) return <Centered>{error}</Centered>
  // D-CRX-83: no standalone empty state; "+ New enrollment" must stay reachable on an empty queue
  // (the getting-started case: candidates present, nobody enrolled yet).

  const fmt = (d?: string) => formatUsDate(d)
  let history: any[] = []
  try { history = detail?.review_history ? JSON.parse(detail.review_history) : [] } catch { history = [] }
  const eghost: React.CSSProperties = { padding: '5px 10px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 12, cursor: 'pointer' }
  const eprimary: React.CSSProperties = { marginTop: 10, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }

  const count = (b: EnrollChip) => chipCount(b, bucketCounts, items)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button onClick={openChooser} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: '#fff' }}>
          + New enrollment
          {candidateTotal > 0 && (
            <span title={`${candidateTotal.toLocaleString()} PA-approved prescription${candidateTotal === 1 ? '' : 's'} not yet in CareRx`}
              style={{ background: '#fff', color: 'var(--color-primary)', borderRadius: 999, padding: '0 7px', fontSize: 11.5, fontWeight: 700 }}>
              {candidateTotal.toLocaleString()} PA approved
            </span>)}
        </button>
      </div>
      {/* D-CRX-83: the chooser is the app's modal panel, not an MUI dialog. The feed's column funnels
          and priority select open in portals outside the panel, which an MUI focus trap would lock
          out and paint underneath. */}
      <SidePanel open={chooserOpen} title="Start a new enrollment"
        subtitle="Pick a PA-approved prescription, or start a manual enrollment" width={1100}
        onClose={() => setChooserOpen(false)}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }} role="group" aria-label="How to start">
          <button type="button" onClick={() => setChooser('pa')} aria-pressed={chooser === 'pa'} style={{
            padding: '6px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${chooser === 'pa' ? 'var(--color-primary)' : 'var(--color-border)'}`,
            background: chooser === 'pa' ? 'var(--color-primary)' : 'var(--color-surface)', color: chooser === 'pa' ? '#fff' : 'var(--color-text)',
          }}>From PA approved ({candidateTotal.toLocaleString()})</button>
          <button type="button" onClick={() => setChooser('manual')} aria-pressed={chooser === 'manual'} style={{
            padding: '6px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${chooser === 'manual' ? 'var(--color-primary)' : 'var(--color-border)'}`,
            background: chooser === 'manual' ? 'var(--color-primary)' : 'var(--color-surface)', color: chooser === 'manual' ? '#fff' : 'var(--color-text)',
          }}>Manual</button>
        </div>
        {chooser === 'manual' ? (
          <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
            <p style={{ margin: '0 0 12px' }}>Start an enrollment for any patient. You pick the patient and the therapy on the form.</p>
            <button onClick={startManual} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: '#fff' }}>
              Start manual enrollment
            </button>
          </div>
        ) : (
        <div>
          {/* D-CRX-35 S1-B (spec Stage 0): the PA feed — one row per patient+drug, DOB + prescriber
              (no GUIDs), search, full list, oldest first. While the ZZTEST fence is ACTIVE only
              synthetic patients get Start; real patients are view-only (spec) and the server refuses
              regardless (S1-A). Once CARERX_ENROLLMENT_FENCE is lifted, Start opens for real
              patients too — this feed reads that state from GET carerx/governance rather than
              assuming it, which is what it did before and why lifting the setting appeared to do
              nothing. Unclassified drugs link to My Pharmacy → Drug list (Patrick ruling). */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>PA approved — not yet in CareRx ({candidateTotal.toLocaleString()})</span>
            <input placeholder="Search patient / DOB / drug / prescriber" value={feedQ} onChange={e => setFeedQ(e.target.value)}
              style={{ padding: '5px 10px', border: '1px solid var(--color-border)', borderRadius: 7, fontSize: 13, minWidth: 240 }} />
            {dismissedCount > 0 && (
              <button onClick={() => setDismissedOpen(true)}
                style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
                Removed from list ({dismissedCount})
              </button>)}
          </div>
          <DismissedCandidatesPanel open={dismissedOpen} onClose={() => setDismissedOpen(false)} onRestored={loadCandidates} />
          {beforeStartLine(rxStart) && (
            <div data-testid="rx-start-line" style={{ fontSize: 12, color: 'var(--color-text-soft, #6b7280)', margin: '4px 0 8px' }}>
              {beforeStartLine(rxStart)}
            </div>)}
          {unqualified > 0 && (
        <div style={{ fontSize: 12, color: 'var(--color-text-soft, #6b7280)', margin: '4px 0 8px' }}>
          {unqualified} PA-approved Rx hidden — medication not qualified (not in Drug Reference, or unclassified). Qualify the drug in Drug Reference to surface them.
        </div>)}
      {unclassified > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginBottom: 8 }}>
              {unclassified} PA-approved drug{unclassified === 1 ? ' is' : 's are'} not yet classified and not shown — classify them in <a href="/settings/pharmacy?tab=drug-reference" style={{ color: 'var(--color-primary)' }}>My Pharmacy → Drug list</a>.
            </div>
          )}
          {/* The 380px inner scroll box predates paging: with a page window the pager below is the
                only navigation this table needs, and an inner scrollbar just hid rows the user had
                already chosen to see ("Show 10 per page" then only 6 visible). Horizontal scroll stays. */}
          {feedFx.chips}
          {candidatesPaged.error != null && (
            <Notice severity="warning">
              The list could not be refreshed{candidates.length > 0 ? ' — showing the last results' : ''}. {friendlyError(candidatesPaged.error)}
            </Notice>
          )}
          <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 9 }}>
            {/* D-CRX-45 Enrollment queue table (mockup design): Patient/Drug · Rx · Written · DOB · Prescriber ·
                Priority (CR-G2) · PA approved (green, aging in BOLD not red) · action. One row per
                patient per PRESCRIPTION (owner ruling 2026-10-01); Start opens the door with that Rx
                anchored. A prescription leaves this list once a therapy covers it. */}
            <table {...feedFx.tableProps} style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Patient / Drug', 'Rx', 'Written', 'DOB', 'Prescriber', 'Priority', 'PA approved', ''].map(h =>
                <th key={h} data-no-funnel={h === 'Priority' ? 'a per-row choice made here, not stored data' : h === 'Rx' ? 'shown for identification; search finds it' : undefined}
                  style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 500, fontSize: 11.5, color: 'var(--color-text-soft, #6b7280)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--color-surface)' }}>{h} {feedFx.funnel(PA_FEED_FUNNELS.find(c => c.label === h)?.key ?? '')}</th>)}</tr></thead>
              <tbody>
                {candidates.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: '12px 10px', color: 'var(--color-text-mute)' }}>
                    {candidatesPaged.loading ? 'Loading…' : feedFx.active ? 'No candidates match the current filters.' : feedQ.trim() ? 'No candidates match this search.' : 'No PA-approved candidates.'}
                  </td></tr>
                )}
                {candidates.map(c => {
                  const ageDays = c.created_at ? Math.floor((Date.now() - new Date(c.created_at as any).getTime()) / 86400000) : null
                  const startDisabled = starting === c.rx_id
                  return (
                    // Same gesture as every other CareRx table: the row is the target. A candidate
                    // has no record yet, so it opens the enrollment door prefilled — exactly what
                    // Start does — and only when Start is actually offered (fence).
                    <tr key={c.rx_id} {...((c.is_synthetic || fenceLifted)
                      ? rowOpen(() => { startFromCandidate(c); }, { borderTop: '1px solid var(--color-border)' })
                      : { style: { borderTop: '1px solid var(--color-border)' } })}>
                      <td style={{ padding: '9px 10px' }}><b>{c.patient_name || 'Patient'}</b><LanguageBadge language={c.preferred_language} />{c.drug_name ? <> — {c.drug_name}</> : ''}</td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace' }}>{c.rx_number || '—'}</td>
                      {/* Sara (BioAccess) 2026-10-28: the date the Rx start date gates on (§4e). */}
                      <td data-testid="pa-feed-written" style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>{c.date_written ? formatUsDate(c.date_written) : '—'}</td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap', color: 'var(--color-text-soft)' }}>{c.date_of_birth ? formatUsDate(c.date_of_birth) : '—'}</td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>{c.prescriber_name || '—'}</td>
                      <td style={{ padding: '9px 10px' }} {...rowActions}>
                        <SearchableSelect value={feedPriority[c.rx_id] ?? 'normal'} onChange={(v: string) => setFeedPriority(p => ({ ...p, [c.rx_id]: v }))}
                          options={[{ value: 'urgent', label: 'Urgent' }, { value: 'high', label: 'High' }, { value: 'normal', label: 'Normal' }, { value: 'low', label: 'Low' }]} />
                      </td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                        {ageDays != null
                          ? <span style={{ color: '#065f46', fontWeight: ageDays >= 7 ? 700 : 600 }}>PA approved {formatUsDate(c.created_at as any)} ({ageDays}d)</span>
                          : <span style={{ color: '#065f46', fontWeight: 600 }}>PA approved</span>}
                      </td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }} {...rowActions}>
                        {/* D-CRX-87b: a pharmacy that combines same-drug Rx attaches this one to the patient's
                            open, not-expired enrollment instead of starting another. */}
                        {(c.is_synthetic || fenceLifted) && c.attach && c.rx_number ? (
                          <AttachRxButton target={c.attach} rxNumber={c.rx_number} drugName={c.drug_name}
                            source="pa_feed" onDone={loadCandidates} onRefused={loadCandidates} />
                        ) : (c.is_synthetic || fenceLifted) ? (
                          <button disabled={startDisabled}
                            style={{ fontSize: 12, fontWeight: 600, border: '1px solid var(--color-primary)', borderRadius: 8, padding: '4px 12px', background: 'var(--color-primary)', color: '#fff', cursor: startDisabled ? 'wait' : 'pointer' }}
                            onClick={() => startFromCandidate(c)}>Start enrollment</button>
                        ) : (
                          <span title="Real-patient enrollment opens when the ZZTEST fence lifts (harness 31/31 + Andrew sign-off)"
                            style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>view-only until go-live</span>
                        )}
                        {/* ASP #14: removing is not enrolling, so it is offered on every row, fenced or not. */}
                        <span style={{ marginLeft: 6 }}>
                          <DismissCandidateButton rxId={c.rx_id} onDone={loadCandidates}
                            label={`${c.patient_name || 'Patient'} — ${c.drug_name || 'drug'} · Rx ${c.rx_number || '—'}`} />
                        </span>
                      </td>
                    </tr>
                  )})}
              </tbody>
            </table>
          </div>
          <Pager {...candidatesPaged.pagerProps} label="prescriptions" />
        </div>
        )}
      </SidePanel>
      {/* Owner tester pass 2026-08-14: closing the door on create HID the routing/success panel
          (and any lab-check warning) — the door now stays open showing it; its Close button closes.
          Candidates refresh too, so an enrolled patient leaves the feed immediately. */}
      {/* D-CRX-80 — the enrollment form is a PANEL, and only one is ever open.
          Both of these used to render INLINE above the queue with nothing preventing both at once,
          so the tab could show two full enrollment forms stacked on each other and push the
          worklist off the bottom of the screen. The panel gives one focused surface, leaves the
          queue where the operator left it, and names the record in a sticky header — on a form
          this long you are usually scrolled past the top, and "which patient is this?" had no
          answer from there. */}
      <SidePanel
        open={doorOpen}
        title="New enrollment"
        subtitle={doorPrefill?.patient?.label ?? null}
        onClose={closeDoor}>
        <EnrollmentDoor
          presetPatient={doorPrefill?.patient ?? null} presetLanguage={doorPrefill?.language} presetFormularyId={doorPrefill?.formularyId}
          presetRxNumber={doorPrefill?.rxNumber} presetPriority={doorPrefill?.priority}
          onClose={closeDoor}
          onCreated={() => { setDoorCreated(true); load(); loadCandidates() }} embedded />
      </SidePanel>
      <SidePanel
        open={!!editTarget && !!sel}
        title="Edit enrollment"
        subtitle={[selLabel, editTarget?.drug_name].filter(Boolean).join(' · ')}
        onClose={() => setEditTarget(null)}>
        {editTarget && sel && <EnrollmentDoor
          presetPatient={selPatient ? identityPreset(selPatient) : null}
          editTherapy={{ therapy_id: editTarget.id, drug_name: editTarget.drug_name, formulary_id: editTarget.formulary_id, site: editTarget.care_delivery_mode ?? null }}
          presetPharmacistId={items.find(i => i.therapy_id === editTarget.id)?.assigned_rph_id ?? null}
          onClose={() => setEditTarget(null)}
          onCreated={() => { setEditTarget(null); load(); if (selPatient) openReview(selPatient) }} embedded />}
      </SidePanel>
      {queueCap !== null && <TruncationNotice shown={queueCap} what="enrollment rows, most recently updated first"
        detail="older enrollments are not listed here; the counts on the buttons below cover the whole queue" />}
      {queueNote && <Notice severity={queueNote.ok ? 'success' : 'error'}>{queueNote.text}</Notice>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {ENROLL_CHIPS.map(([k, label]) => (
          // title doubles as the accessible description; the name stays "<label> (n)".
          <button key={k} onClick={() => setBucket(k)} title={CHIP_HINT[k]} style={{
            padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${bucket === k ? 'var(--color-primary)' : 'var(--color-border)'}`,
            background: bucket === k ? 'var(--color-primary)' : 'var(--color-surface)', color: bucket === k ? '#fff' : 'var(--color-text)',
          }}>{label} ({count(k)})</button>
        ))}
      </div>
      {CHIP_HINT[bucket] && <div data-chip-hint style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '-6px 0 10px' }}>{CHIP_HINT[bucket]}</div>}
      {queueFx.chips}
      <div style={{ overflowX: 'auto' }}><table {...queueFx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
        {/* Owner review 2026-08-13: Episode column dropped — never populated, burned table width. */}
        <thead><tr>{['Patient', 'Enrollment ref', 'Drug / archetype', 'Status', 'Pharmacist', 'Assigned nurse', 'Clinical notes', 'Last review', ''].map(h => <th key={h} style={th}>{h} {queueFx.funnel(h)}</th>)}</tr></thead>
        <tbody>
          {queueFx.rows.length === 0 && <tr><td colSpan={9} style={{ ...td, color: 'var(--color-text-mute)' }}>{shown.length === 0 ? 'No enrollments in this view.' : 'No rows match the current filters.'}</td></tr>}
          {queueFx.rows.map((e, i) => (
            // Selection must read as selected against the zebra stripe — --color-bg (#fafaf9)
            // was a hair off --color-surface-alt (#f5f5f4) and vanished on striped rows.
            // ASP #6: an open therapy screened pregnant (UNIV-09 = YES) turns the row yellow; the
            // badge says why, so the colour is never the only signal. Selection still wins.
            <tr key={i} {...rowOpen(() => openReview(queueIdentity(e)), { borderBottom: '1px solid var(--color-border)', background: sel === e.patient_id ? 'var(--color-primary-soft)' : e.pregnancy_yes ? PREGNANT_ROW_BG : undefined })}
                data-pregnancy={e.pregnancy_yes ? 'yes' : undefined}>
              <td data-label="Patient" style={td}>
                {e.patient_name || '—'}<LanguageBadge language={e.preferred_language} />
                {e.pregnancy_yes && <span style={pregnantBadge} title="Screening UNIV-09: pregnant or pregnancy suspected">Pregnant</span>}
                {/* ASP-CRX-08: human identifiers — DOB, city (Pioneer-owned, display only), Rx number(s) */}
                <div style={{ fontSize: 10.5, color: 'var(--color-text-mute)' }}>
                  {[e.date_of_birth ? `DOB ${formatUsDate(e.date_of_birth)}` : null, e.city || null, e.rx_numbers ? `Rx ${e.rx_numbers}` : null].filter(Boolean).join(' · ') || '—'}
                </div>
              </td>
              <td data-label="Enrollment ref" style={td}><TherapyRefTag refs={e.therapy_refs} /></td>
              <td data-label="Drug / archetype" style={td}>
                {e.drug_name
                  ? <>{e.drug_name}{e.open_therapies > 1 ? <span style={{ color: 'var(--color-text-mute)', fontSize: 11 }}> · {e.open_therapies} therapies</span> : null}
                      {e.therapy_archetype ? <div style={{ fontSize: 10.5, color: 'var(--color-text-mute)' }}>{e.therapy_archetype.replace(/_/g, ' ')}</div> : null}</>
                  : <span style={{ color: 'var(--color-text-mute)' }}>—</span>}
              </td>
              {/* D-CRX-84 R2: where THIS prescription is now; the stored enrollment record is the tooltip. */}
              <td data-label="Status" style={td}><EnrollmentStageStatus row={e} /></td>
              {/* Owner 2026-10-04: the row's pharmacist; assigning here names them without starting the review. */}
              <td data-label="Pharmacist" style={td} {...rowActions}>
                {e.therapy_id ? (
                  <>
                    {!e.assigned_rph_id && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#b23a48' }}>Unassigned</span>}
                    <ReviewAssignee therapyId={e.therapy_id} rphId={e.assigned_rph_id ?? null} rphName={e.pharmacist_name ?? null}
                      assignedAt={null} keepStatus
                      onSelf={async () => {
                        if (!me?.account_id || !e.therapy_id) return
                        try { await assignReviewTo(e.therapy_id, me.account_id, true); load() }
                        catch (err) { setMsg(friendlyError(err)) }
                      }}
                      onChanged={() => load()} />
                  </>
                ) : <span style={{ color: 'var(--color-text-mute)' }}>—</span>}
              </td>
              <td data-label="Nurse" style={td}>{e.nurse_name || (e.assigned_nurse_id ? 'assigned' : '—')}</td>
              {/* Owner 2026-10-01: long notes are cut in the cell; the tooltip shows them complete. */}
              <td data-label="Clinical notes" style={{ ...td, maxWidth: 220 }}><TruncatedText text={e.intake_notes} label="Clinical notes" /></td>
              <td data-label="Last review" style={{ ...td, maxWidth: 220 }}>
                <TruncatedText text={e.last_review_note} label="Last review"
                  heading={e.last_review_note ? `${e.last_review_by || 'Reviewer'} · ${formatUsDateTime(e.last_review_at)}` : null} />
              </td>
              {/* D-CRX-80: the actions an operator reaches for on EVERY row live here now, not in
                  a panel that had to be opened first. Documents are therapy-grain, so a patient
                  with exactly one open therapy gets them inline; more than one still resolves in
                  the review dialog, because guessing which therapy a document is for is the one
                  mistake that produces a wrong clinical document. */}
              <td data-label="" style={{ ...td, whiteSpace: 'nowrap' }} {...rowActions}>
                <button style={eghost} onClick={() => openReview(queueIdentity(e))}>Review</button>
                {e.therapy_id && (
                  <span style={{ marginLeft: 6, display: 'inline-flex', gap: 6 }}>
                    <TherapyDocumentButtons therapyId={e.therapy_id} buttonStyle={eghost} onError={setMsg} />
                    {/* D-CRX-84 R2 (board Q5): the real workflow step for this row's stage — never a status edit. */}
                    <EnrollmentStageAction row={e} buttonStyle={eghost} onDone={(text, ok) => setQueueNote({ text, ok })} />
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {/* D-CRX-80 — the review is a DIALOG, not a third panel competing for the page.
          It used to render inline under the queue at maxWidth 620, so opening a review pushed the
          worklist down and sat alongside whichever enrollment form happened to be open. Its
          read-only facts, notes and history need room; its ACTIONS belong on the row and are now
          there. Same SidePanel as the enrollment form, so the tab has one way of showing detail. */}
      {/* D-CRX-80 — the soft-delete confirm. It states what is RETAINED, because "delete" on a
          clinical record reasonably reads as destruction and this is not that: the therapy, its
          labs, holds, documents and any signed envelope stay for the HIPAA record. The reason is
          required by the server too — this is not a client-side courtesy. */}
      {deleteTarget && (
        <div role="presentation"
             onMouseDown={e => { if (e.target === e.currentTarget) setDeleteTarget(null) }}
             style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,15,0.45)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div role="alertdialog" aria-modal="true" aria-label="Delete enrollment"
               style={{ background: 'var(--color-surface, #fff)', borderRadius: 12, maxWidth: 520,
                        width: '100%', padding: '20px 22px', boxShadow: '0 18px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ fontWeight: 700, fontSize: 15.5, color: '#b91c1c' }}>Delete enrollment</div>
            <p style={{ margin: '10px 0 6px', fontSize: 13.5, lineHeight: 1.55 }}>
              <b>{deleteTarget.drug_name ?? 'This therapy'}</b> for <b>{selLabel}</b> leaves
              the CareRx queues.
            </p>
            <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--color-text-soft, #555)' }}>
              Its labs, holds, documents and any signed envelope are <b>retained</b> for the clinical
              and HIPAA record. Its open POT visits are <b>cancelled</b> and any form still out for
              signature is <b>voided</b> at DocuSign. This patient can be enrolled on the same drug again afterwards.
            </p>
            <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>Reason (required)</label>
            <textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
              placeholder="e.g. wrong drug selected at intake"
              style={{ width: '100%', minHeight: 60, padding: '8px 10px', border: '1px solid var(--color-border)',
                       borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
                       background: 'var(--color-surface)', color: 'var(--color-text)' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button style={eghost} disabled={deleteBusy} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button disabled={deleteBusy || !deleteReason.trim()}
                style={{ ...eprimary, background: '#b91c1c', borderColor: '#b91c1c',
                         opacity: deleteBusy || !deleteReason.trim() ? 0.55 : 1 }}
                onClick={async () => {
                  setDeleteBusy(true)
                  try {
                    const r = await api.deleteCareRxEnrollment(deleteTarget.id, deleteReason.trim())
                    setDeleteTarget(null); setDeleteReason('')
                    // On the QUEUE, not in the review panel: the delete closes that panel (setSelPatient(null)),
                    // so a message set there was never seen — and an envelope DocuSign would not void
                    // is a warning the operator has to act on (go-live #3).
                    setQueueNote({ text: deleteOutcome(r), ok: (r.envelopes_not_voided ?? []).length === 0 })
                    setSelPatient(null); load()
                  } catch (e) {
                    // 409 already_deleted means the row on screen is stale, not that the operator
                    // did anything wrong — the delete had already gone through. Close, refresh and
                    // say so, rather than leaving them staring at a row that will 409 forever.
                    const body = (e as { body?: { error?: string } })?.body
                    if (body?.error === 'already_deleted') {
                      setDeleteTarget(null); setDeleteReason(''); setSelPatient(null); load()
                      setQueueNote({ text: 'That enrollment was already deleted. The list has been refreshed.', ok: true })
                    } else {
                      setMsg(e instanceof Error ? e.message : 'Delete failed')
                    }
                  } finally { setDeleteBusy(false) }
                }}>
                {deleteBusy ? 'Deleting…' : 'Delete enrollment'}
              </button>
            </div>
          </div>
        </div>
      )}
      <SidePanel
        open={!!sel && !doorOpen && !editTarget && !chooserOpen}
        title="Enrollment review"
        subtitle={selPatient ? selLabel : null}
        onClose={() => setSelPatient(null)}
        width={720}>
        {sel && (<>
          {detail?.approved_at ? <div style={{ fontSize: 12, color: '#15803d', marginBottom: 8 }}>Approved · {fmt(detail.approved_at)}</div> : null}
          {/* Owner review 2026-08-13 (D-1): the review panel was blind to the intake it reviews —
              show the captured Intake-A data READ-ONLY. Outreach renders its status-matched date
              (the R4 pairing): reached -> date reached; unable to reach / in progress -> next attempt. */}
          {detail && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface-2, #f9fafb)', marginBottom: 10, fontSize: 13 }}>
              <KV k="Dose / SIG" v={detail.dose_sig} />
              <KV k="Outreach" v={detail.outreach_status === 'reached' ? `Reached · ${fmt(detail.outreach_reached_at)}`
                : detail.outreach_status === 'unable_to_reach' ? `Unable to reach · retry ${fmt(detail.outreach_next_at)}`
                : detail.outreach_status === 'in_progress' ? `In progress${detail.outreach_next_at ? ` · next ${fmt(detail.outreach_next_at)}` : ''}`
                : detail.outreach_status ? String(detail.outreach_status).replace(/_/g, ' ') : undefined} />
              <KV k="Next infusion" v={detail.next_infusion_at ? fmt(detail.next_infusion_at) : undefined} />
              <KV k="Deliver on" v={detail.deliver_on ? fmt(detail.deliver_on) : undefined} />
              <KV k="City" v={detail.city} />
              <KV k="Zip" v={detail.zip} />
            </div>)}
          {/* UAT 2026-07-29 #5: per-therapy Edit + Draft PDF, straight from the queue. */}
          {selTherapies.length > 0 && (
            <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {selTherapies.map(t => (
                <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{t.drug_name ?? 'Drug not set'}</span>
                  {/* One enrollment per Rx (owner 2026-10-01): two lanes of one drug differ by Rx. */}
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>Rx {t.rx_number || '—'}</span>
                  <TherapyRefTag therapyId={t.id} />
                  {/* D-CRX-87b: the Rx attached to this enrollment + "Expires …" (nothing when not set). */}
                  <TherapyRxList therapyId={t.id} onChanged={() => { load(); loadCandidates() }} />
                  <span style={{ color: 'var(--color-text-mute)' }}>{t.state}</span>
                  <button style={eghost} onClick={() => { setDoorOpen(false); setDoorPrefill(null); setEditTarget(t) }}>Edit enrollment</button>
                  {/* D-CRX-80 (mig 386): SOFT delete, Manager and above. Gated on the permission
                      rather than on a role name, so the grant is the single source of truth.
                      Nothing is destroyed — the therapy, its labs, holds, documents and any signed
                      envelope are retained; the enrollment simply leaves the queues, and the same
                      patient can be enrolled on the same drug again. */}
                  {hasPermission('carerx.enrollment.delete') && (
                    <button style={{ ...eghost, borderColor: '#b91c1c', color: '#b91c1c' }}
                      onClick={() => { setDeleteTarget(t); setDeleteReason('') }}>Delete enrollment</button>
                  )}
                  {/* D-CRX-63: both documents come from one shared unit (L-067), so every stage of
                      the pipeline offers the same set instead of each screen having its own. */}
                  <TherapyDocumentButtons therapyId={t.id} buttonStyle={eghost} onError={setMsg} />
                </div>))}
            </div>)}
          {/* FALLBACK ONLY (D-CRX-80). The rows above list only NON-terminal therapies, so a
              patient whose therapy has ended would otherwise get no document button at all — and
              a patient with none gets no explanation. This patient-grain control resolves every
              therapy (ended ones included), asks which when there are several, and says why when
              there are none.
              It is rendered ONLY when those rows are empty: mounting both put two sets of the
              same two buttons on one panel, which is what "Enrollment PDF" and "Enrollment
              packet" sitting side by side actually was. */}
          {selTherapies.length === 0 && <PatientEnrollmentPdfButton patientId={sel} />}

          {/* UAT 2026-07-30 (Patrick): the review opens the enrollment READ-ONLY. No approve /
              request-changes / reject actions here — use "Edit enrollment" above to unlock and make
              changes. Review notes remain for the record: their own thread (mig 436), newest first,
              paged on the server — adding one never edits the clinical (intake) notes or a note. */}
          <EnrollmentReviewNotes key={sel} patientId={sel} onAdded={load} />
          {msg && <div style={{ fontSize: 12, marginTop: 8, color: msg === 'Recorded.' ? 'var(--color-primary)' : '#dc2626' }}>{msg}</div>}
          {/* Owner review 2026-08-13 (D-2): the home-meds editor is gone — R-18 retired home meds
              from enrollment (Pioneer allergies replace them); the patient-chart surface keeps the
              carerx_home_meds model. Editing here contradicted the read-only review ruling too. */}

          {history.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-mute)', marginBottom: 4 }}>Review history</div>
              {history.map((h, i) => (
                <div key={i} style={{ fontSize: 12, padding: '5px 0', borderTop: '1px solid var(--color-border)' }}>
                  <strong style={{ textTransform: 'capitalize' }}>{String(h.action || '').replace('_', ' ')}</strong> · {fmt(h.at)}{h.notes ? ` — ${h.notes}` : ''}
                </div>
              ))}
            </div>
          )}
        </>)}
      </SidePanel>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--color-text-mute)', fontSize: 14, display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>{children}</div>
}
function KV({ k, v }: { k: string; v?: any }) {
  return <div><span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: .4 }}>{k}</span><span>{v || '—'}</span></div>
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '2px solid var(--color-border)', whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '9px 10px' }
const panel: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, background: 'var(--color-surface)' }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const ghostBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, cursor: 'pointer' }
